import {inject, InjectionKey, onMounted, onUnmounted, provide} from "vue";

type Handler = (ev: KeyboardEvent) => void;

/**
 * Creates a keyboard capture context. Call this in a components setup to listen for global keyboard events.
 * Any component that's a child of this component can use onKeyboard method to retrieve the captured keyboard events.
 * 
 * @param filter A function to filter keyboard events, to either temporarily disable keyboard capture or only capture certain keystrokes.
 */
export function createKeyboardCaptureContext(
  filter: (ev: KeyboardEvent) => boolean
) {
  
  const keydownHandlers = new Map<string, Handler>();
  let idCounter = 0;
  
  const context: KeyboardContext = {
    registerKeydown: (handler) => {
      const id = `h${idCounter++}`;
      keydownHandlers.set(id, handler);
      return id;
    },
    unregisterKeydown(id: string) {
      keydownHandlers.delete(id);
    }
  }
  
  provide(keyboardContextKey, context);
  
  onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
  });
  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
  });
  
  function handleKeydown(ev: KeyboardEvent) {
    if (filter && !filter(ev)) {
      return;
    }
    keydownHandlers.forEach(handler => handler(ev));
  }
}

/**
 * Registers a keydown handler that trigger from a parent keyboard capture context
 * @param handler
 */
export function onKeydown(handler: (e: KeyboardEvent) => void) {
  const keyboardContext = inject(keyboardContextKey);
  if (!keyboardContext) {
    console.error('onKeydown was called but there was no keyboard context');
    return;
  }
  
  let handlerId: string | undefined;
  onMounted(() => {
    keyboardContext.registerKeydown(handler);
  });
  onUnmounted(() => {
    if (handlerId) {
      keyboardContext.unregisterKeydown(handlerId);
    }
  })
}

interface KeyboardContext {
  registerKeydown(handler: (e: KeyboardEvent) => void): string;
  unregisterKeydown(id: string): void;
}

const keyboardContextKey: InjectionKey<KeyboardContext> = Symbol('keyboardContextKey');